import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useAccount } from '@/frontastic';
import { combinedKeyPressHandler, getCurrentDateTime, getAgentName } from '@/helpers/utils/siteUtils';
import { UpdateOrderPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { IsShippingInclude } from '../../../../../constants/constant';
import { PAYLOAD_QUERY, RESPONSE_SUCCESS_MESSAGE } from '../../customer-detail/constants';
import { ADJUST_ORDER_CONSTANTS } from '../../order-details/constant';
import { adjustOrderSchema } from '../../order-details/schema';
import { OrderSummaryInfo } from '../../order-details/types';
import { Button } from '../../ui/button';
import InputField from '../../ui/customInput/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/customSelect/customSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { Textarea } from '../../ui/textarea';

interface AdjustOrder {
  Texts: { [key: string]: string };
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  isOrderShipped: boolean;
  addNewNotes: any;
  EstimatedTotal: OrderSummaryInfo;
  getUpdatedData: () => void;
}

const AdjustOrderModal = ({
  Texts,
  open,
  setOpen,
  isOrderShipped,
  addNewNotes,
  EstimatedTotal,
  getUpdatedData,
}: AdjustOrder) => {
  const [selectedAdjustment, setSelectedAdjustment] = useState('');
  const [calculateAmount, setCalculateAmount] = useState('0.00');
  const [error, setError] = useState<any>({});
  const { updateOrder, addOrderNote } = useAccount();
  const params = useSearchParams();
  const [loading, setLoading] = useState(false);
  const subtotal = EstimatedTotal?.merchandiseAmt ?? 0;
  const [agentName] = useState(getAgentName().replace(/@.*/, ''));

  const handleDollarAmountChange = (e: any) => {
    const inputValue = e.target.value;
    if (subtotal > 1000 && parseFloat(inputValue) > 1000) {
      setError({ ...error, dollarAmount: Texts.adjustmentAmountExceedThreshold });
    } else if (inputValue > subtotal) {
      setError({
        ...error,
        dollarAmount: Texts.adjustmentAmountExceedSubtotal,
      });
    } else {
      setError({});
    }
  };

  const handlePercentOffChange = (e: any) => {
    const inputValue = e.target.value;
    const adjustmentAmount = (subtotal * (inputValue / 100)).toFixed(2);
    setCalculateAmount(adjustmentAmount);
    if (subtotal > 1000 && parseFloat(adjustmentAmount) > 1000) {
      setError({ ...error, percentAmount: Texts.adjustmentAmountExceedThreshold });
    } else if (parseFloat(adjustmentAmount) > subtotal) {
      setError((prev: any) => ({
        ...prev,
        percentAmount: Texts.adjustmentAmountExceedSubtotal,
      }));
    } else {
      setError({});
    }
  };

  const handleShippingAdjustmentAmount = () => {
    setError({});
  };
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    unregister,
    clearErrors,
  } = useForm({
    resolver: zodResolver(adjustOrderSchema),
    defaultValues: {
      selectedAdjustment: undefined,
      selectedReason: undefined,
      notes: '',
      percentAmount: '',
      dollarAmount: '',
      shippingAmount: '0',
    },
    mode: 'all',
  });
  const onSubmit = async (data: any) => {
    if (Object.keys(error)?.length > 0) {
      return;
    }

    setLoading(true);
    let amount;
    if (data?.selectedAdjustment === Texts.DOLLAR_OFF) {
      amount = data?.dollarAmount;
    } else if (data?.selectedAdjustment === Texts.PERCENT_OFF) {
      amount = calculateAmount;
    } else {
      amount = data?.shippingAmount;
    }
    const payload = {
      query: {
        orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      },
      data: {
        Recipients: [
          {
            LineItems: [
              {
                ItemPriceInfo: {
                  unitPrice: `-${amount}`,
                  PriceAdjustments: {
                    adjType: data?.selectedAdjustment,
                    reasonDescription: data?.selectedReason,
                  },
                },
              },
            ],
          },
        ],
      },
    } as UpdateOrderPayload;

    const updateOrderRes = await updateOrder(payload);

    if (updateOrderRes && updateOrderRes?.code === RESPONSE_SUCCESS_MESSAGE) {
      const currentDate = getCurrentDateTime();
      const payload = {
        data: {
          Note: {
            createdBy: agentName,
            createdDate: currentDate,
            isPriority: true,
            text: data?.notes ?? '',
          },
        },
        query: {
          orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
          divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        },
      };

      await addOrderNote(payload).then(() => {
        addNewNotes();
        getUpdatedData();
        setOpen(false);
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedAdjustment === Texts.DOLLAR_OFF) {
      unregister('percentAmount');
      unregister('shippingAmount');
      clearErrors();
    } else if (selectedAdjustment === Texts.PERCENT_OFF) {
      unregister('dollarAmount');
      unregister('shippingAmount');
      clearErrors();
    } else if (IsShippingInclude.includes(selectedAdjustment)) {
      unregister('dollarAmount');
      unregister('percentAmount');
      clearErrors();
    }
  }, [selectedAdjustment]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-[667px] bg-white sm:rounded-md">
        {loading && <Loader />}
        <DialogHeader>
          {/* chng-text-25px */}
          <DialogTitle className="text-2xl font-bold">
            <section>
              <div className="text-2xl font-bold">{Texts.adjustOrderText}</div>
              <div className="text-xs font-normal">{Texts.requiredFields}</div>
            </section>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <section className="mb-4">
            <section className="mb-4 flex w-full gap-4">
              <section className="flex w-1/2 flex-col">
                <Controller
                  name={'selectedAdjustment'}
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={(value) => {
                        setSelectedAdjustment(value);
                        field.onChange(value);
                      }}
                    >
                      <SelectTrigger
                        className={`mr-2 w-full ${
                          errors?.selectedAdjustment?.message?.toString() ? 'border-scarletRed text-scarletRed' : ''
                        } `}
                      >
                        <SelectValue placeholder="*Select Adjustment" />
                      </SelectTrigger>
                      <SelectContent className="w-[90%] bg-white px-4">
                        {ADJUST_ORDER_CONSTANTS.ADJUSTMENTS.map((adjustment) => (
                          <SelectItem key={`key-${adjustment?.key}`} value={adjustment.value}>
                            {adjustment.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <div className="font-normal text-scarletRed">{errors?.selectedAdjustment?.message?.toString()}</div>
              </section>
              <section className="flex w-1/2 flex-col">
                <Controller
                  name={'selectedReason'}
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange}>
                      <SelectTrigger
                        className={`mr-2 w-full ${
                          errors?.selectedReason?.message?.toString() ? 'border-scarletRed text-scarletRed' : ''
                        } `}
                      >
                        <SelectValue placeholder="*Select Reason" />
                      </SelectTrigger>
                      <SelectContent className="w-[90%] bg-white px-4">
                        {ADJUST_ORDER_CONSTANTS.REASONS.map((reason) => (
                          <SelectItem key={`key-${reason?.key}`} value={reason.value}>
                            {reason.value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <div className="font-normal text-scarletRed">{errors?.selectedReason?.message?.toString()}</div>
              </section>
            </section>
            <section className="mb-4">
              {selectedAdjustment === Texts.DOLLAR_OFF && (
                <>
                  <section className="flex w-full max-w-[220px] items-baseline gap-4">
                    <InputField
                      items={Texts.DOLLAR_OFF}
                      classname="w-full"
                      monetaryInput
                      register={register}
                      errors={errors?.['dollarAmount'] ? errors : error}
                      label={'Dollar Amount'}
                      id={'dollarAmount'}
                      htmlFor="dollarAmount"
                      maxLength={10}
                      onChange={handleDollarAmountChange}
                    />
                    <div>$</div>
                  </section>
                  <section className="text-scarletRed">{error?.dollarAmount}</section>
                </>
              )}
              {selectedAdjustment === Texts.PERCENT_OFF && (
                <>
                  <section className="flex items-baseline gap-4">
                    <section className="flex w-full max-w-[220px] items-baseline gap-4">
                      <InputField
                        items={Texts.PERCENT_OFF}
                        classname="w-full"
                        monetaryInput
                        register={register}
                        errors={errors?.['percentAmount'] ? errors : error}
                        label={'Percent Amount'}
                        id={'percentAmount'}
                        htmlFor="percentAmount"
                        maxLength={10}
                        onChange={handlePercentOffChange}
                      />
                    </section>

                    <div>%</div>
                    <div>${calculateAmount}</div>
                  </section>
                  <section className="text-scarletRed">{error?.percentAmount}</section>
                </>
              )}
              {IsShippingInclude.includes(selectedAdjustment) && isOrderShipped && (
                <>
                  <section className="flex w-[220px] max-w-[full] items-baseline gap-4">
                    <InputField
                      items={Texts.SHIPPING_OFF}
                      classname={'w-full'}
                      monetaryInput
                      register={register}
                      errors={errors?.['shippingAmount'] ? errors : error}
                      label={'Shipping Amount'}
                      id={'shippingAmount'}
                      htmlFor={'shippingAmount'}
                      maxLength={10}
                      onChange={handleShippingAdjustmentAmount}
                    />
                    <div>$</div>
                  </section>
                  <section className="text-scarletRed">{error?.shippingAmount}</section>
                </>
              )}
            </section>
            <section className="mb-4 h-[114px]">
              <Textarea
                placeholder="Your Notes Here..."
                className={`h-full resize-none ${
                  errors?.notes?.message?.toString()
                    ? 'border-scarletRed text-scarletRed placeholder:text-scarletRed'
                    : ''
                }`}
                maxLength={120}
                onKeyPress={combinedKeyPressHandler}
                {...register('notes')}
              />
            </section>
            <div className="font-normal text-scarletRed">{errors?.notes?.message?.toString()}</div>
          </section>
          <section className="mt-4 flex justify-start gap-4">
            <Button className="rounded-md bg-deepBlue p-4 text-white" type="submit">
              {Texts.applyAdjustment}
            </Button>
            <Button className="p-0 underline" onClick={() => setOpen(false)}>
              {Texts.cancel}
            </Button>
          </section>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdjustOrderModal;
